import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./PricingScreen.module.css";
import {
  FaCopy,
  FaPaypal,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
} from "react-icons/fa";
import { getDatabase, ref, get, set, push } from "firebase/database";
import { trackButtonClick, trackFeatureUsage } from "../utils/analytics";
import { handleAffiliateLink } from "../utils/affiliateUtils";
import {
  trackEvent,
  trackInitiateCheckout,
  trackPurchase,
} from "../utils/metaPixel";
import { Helmet } from "react-helmet-async";

const COMMENT_FEATURE_ALLOWED_MEMBERSHIPS = [2, 3, 4, 6];

const PricingScreen = () => {
  const siteUrl = "https://autowork.com.vn";
  const canonicalUrl = `${siteUrl}/pricing`;
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [isYearly, setIsYearly] = useState(false);
  const [showPointsPopup, setShowPointsPopup] = useState(false);

  // Đồng bộ billingCycle với isYearly làm fallback cho các logic cũ và Meta Pixel
  useEffect(() => {
    if (billingCycle === "yearly") {
      setIsYearly(true);
    } else {
      setIsYearly(false);
    }
  }, [billingCycle]);
  const [selectedPointPackage, setSelectedPointPackage] = useState(null);
  const [isClosingPoints, setIsClosingPoints] = useState(false);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [membershipExpiration, setMembershipExpiration] = useState(null);
  const [membershipIsYearly, setMembershipIsYearly] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState("vi");
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState(null);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Kiểm tra ngôn ngữ hiện tại từ localStorage
  useEffect(() => {
    const savedLangCode = localStorage.getItem("selectedLanguageCode");
    if (savedLangCode) {
      setCurrentLanguage(savedLangCode);
    }

    // Lấy userId từ localStorage
    const userIdFromStorage = localStorage.getItem("userId");
    if (userIdFromStorage) {
      setUserId(userIdFromStorage);
    }
  }, []);

  // Xác định ký hiệu tiền tệ dựa vào ngôn ngữ
  const getCurrencySymbol = () => {
    return currentLanguage === "vi" ? "₫" : "$";
  };

  // Chuyển đổi giá từ VND sang USD (đơn giản, chỉ để minh họa)
  const convertPrice = (priceInVND) => {
    // Kiểm tra nếu giá trị đầu vào là undefined hoặc null
    if (!priceInVND) {
      console.warn("Giá trị priceInVND không hợp lệ:", priceInVND);
      return currentLanguage === "vi" ? "0" : 0;
    }

    if (currentLanguage === "vi") {
      return priceInVND.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
    // Giả sử tỷ giá 1 USD = 25.000 VND
    const exchangeRate = 25000;
    const priceNumber = parseInt(priceInVND.replace(/\./g, ""), 10);
    return Math.ceil(priceNumber / exchangeRate);
  };

  const checkLoginStatus = () => {
    const accessToken = localStorage.getItem("userAccessToken");
    return !!accessToken;
  };

  const plans = [
    {
      id: 6,
      name: "Trải nghiệm",
      price: "29.000",
      monthlyPoints: 200,
      features: [
        "Tạo bài viết từ chủ đề yêu cầu",
        "Tạo bài viết từ ảnh sản phẩm",
        "AI tự động viết và đăng bài đa kênh (Facebook, Zalo OA)",
        "200 điểm (~20 bài viết)",
        {
          text: "Miễn phí trải nghiệm khi đăng ký tài khoản ",
          highlight: true,
        },
      ],
      recommended: false,
      isShortTerm: true,
    },
    {
      id: 1,
      name: "Cơ bản",
      price: "79.000",
      priceYear: "710.000",
      originalYearPrice: "948.000",
      yearlyDiscount: 25,
      monthlyPriceInYearlyPlan: "59.000",
      price3Months: "219.000",
      original3MonthsPrice: "237.000",
      discount3Months: 10,
      monthlyPriceIn3MonthsPlan: "73.000",
      monthlyPoints: 1000,
      features: [
        "Tạo bài viết từ chủ đề yêu cầu",
        "Tạo bài viết từ ảnh sản phẩm",
        "AI tự động viết và đăng bài đa kênh (Facebook, Zalo OA)",
        "1000 điểm/tháng (~100 bài viết)",
        "⚠️ Chỉ đăng trực tiếp (Không có chế độ duyệt)",
        "⚠️ Chỉ hỗ trợ tải ảnh (Không hỗ trợ video)",
      ],
      recommended: true,
    },
    {
      id: 2,
      name: "Chuyên nghiệp",
      price: "179.000",
      priceYear: "1.610.000",
      originalYearPrice: "2.148.000",
      yearlyDiscount: 25,
      monthlyPriceInYearlyPlan: "134.000",
      price3Months: "489.000",
      original3MonthsPrice: "537.000",
      discount3Months: 10,
      monthlyPriceIn3MonthsPlan: "163.000",
      monthlyPoints: 2900,
      features: [
        "Tất cả tính năng Cơ bản",
        "2900 điểm/tháng (~290 bài viết)",
        "🎬 Tải video lên để tạo bài viết với AI",
        "👁 Chế độ Duyệt bài viết trước khi đăng tự động",
        "Ưu tiên hỗ trợ và hướng dẫn",
        { text: "❌ Không có: Tạo ảnh AI & Tích hợp Website", highlight: false },
      ],
      recommended: false,
    },
    {
      id: 3,
      name: "Cao Cấp",
      price: "249.000",
      priceYear: "2.240.000",
      originalYearPrice: "2.988.000",
      yearlyDiscount: 25,
      monthlyPriceInYearlyPlan: "187.000",
      price3Months: "679.000",
      original3MonthsPrice: "747.000",
      discount3Months: 10,
      monthlyPriceIn3MonthsPlan: "226.000",
      monthlyPoints: 4300,
      features: [
        "Tất cả tính năng gói Chuyên nghiệp",
        "4300 điểm/tháng (~430 bài viết)",
        "🎨 🤖 Tạo ảnh AI tự động cho bài viết",
        "🌐 AI Viết Bài Chuyên Sâu cho Website WordPress",
        "🌐 Đăng bài tự động lên website WordPress",
        "Ưu tiên hỗ trợ tích hợp Website & Zalo OA",
      ],
      recommended: false,
    },
    {
      id: 4,
      name: "VIP",
      price: "Liên hệ",
      monthlyPoints: 7200,
      features: [
        "Tất cả tính năng Cao Cấp",
        "Số lượng điểm tùy chỉnh",
        "Đào tạo và hỗ trợ riêng",
        "Tích hợp theo yêu cầu",
        "Nhiều ưu đãi khác",
      ],
      recommended: false,
      isCustom: true,
    },
  ];

  const pointPackages = [
    {
      name: "Gói Tiết Kiệm",
      points: 300,
      price: "25.000", // 300 điểm × 90₫/điểm = 27.000₫ (90₫/điểm - cao hơn gói Cơ bản một chút)
      description: "Phù hợp cho người dùng gói Cơ bản cần thêm ~30 bài viết",
    },
    {
      name: "Gói Tiêu Chuẩn",
      points: 700,
      price: "55.000", // 700 điểm × 80₫/điểm = 56.000₫ (80₫/điểm - tương đương gói Cơ bản)
      description: "Bổ sung thêm ~70 bài viết cho tài khoản",
    },
    {
      name: "Gói Cao Cấp",
      points: 2000,
      price: "120.000", // 2000 điểm × 60₫/điểm = 120.000₫ (60₫/điểm - cao hơn gói Chuyên nghiệp một chút)
      description: "Bổ sung thêm ~200 bài viết cho tài khoản",
    },
    {
      name: "Gói Doanh Nghiệp",
      points: 5000,
      price: "250.000", // 5000 điểm × 50₫/điểm = 250.000₫ (50₫/điểm - cao hơn gói Cao cấp một chút)
      description: "Dành cho người dùng chuyên nghiệp cần số lượng lớn",
    },
  ];

  const commentPackages = [
    {
      id: 1,
      name: "Gói Nhỏ",
      comments: 500,
      price: "29.000",
      description: "Thêm 500 lượt AI Auto Comment",
    },
    {
      id: 2,
      name: "Gói Vừa",
      comments: 2000,
      price: "99.000",
      description: "Thêm 2,000 lượt AI Auto Comment",
    },
    {
      id: 3,
      name: "Gói Lớn",
      comments: 5000,
      price: "199.000",
      description: "Thêm 5,000 lượt AI Auto Comment",
    },
  ];

  useEffect(() => {
    // Xử lý affiliate link khi vào trang pricing
    handleAffiliateLink();

    const fetchMembership = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) {
        const db = getDatabase();
        const membershipRef = ref(db, `/users/${userId}/membership`);
        const expirationRef = ref(db, `/users/${userId}/membershipExpiry`);
        const membershipTypeRef = ref(db, `/users/${userId}/membership`);
        try {
          const membershipSnapshot = await get(membershipRef);
          if (membershipSnapshot.exists()) {
            // Normalize to number — admin PATCH may store as string "3" vs payment stores as number 3
            const rawVal = membershipSnapshot.val();
            setCurrentMembership(typeof rawVal === "string" ? parseInt(rawVal, 10) : rawVal);
          }

          const expirationSnapshot = await get(expirationRef);
          if (expirationSnapshot.exists()) {
            setMembershipExpiration(expirationSnapshot.val());
          }

          const membershipTypeSnapshot = await get(membershipTypeRef);
          if (membershipTypeSnapshot.exists()) {
            // Giả sử "yearly" hoặc "annual" là giá trị cho gói hàng năm
            const isYearly =
              membershipTypeSnapshot.val() === "yearly" ||
              membershipTypeSnapshot.val() === "annual";
            setMembershipIsYearly(isYearly);
          }
        } catch (error) {
          console.error("Lỗi khi lấy thông tin membership:", error);
        }
      }
    };

    fetchMembership();
  }, []);

  const isCommentFeatureEligibleMembership =
    COMMENT_FEATURE_ALLOWED_MEMBERSHIPS.includes(Number(currentMembership));
  const isCommentQuotaPurchaseLocked = Boolean(userId) && !isCommentFeatureEligibleMembership;

  useEffect(() => {
    // Kiểm tra trạng thái đơn hàng
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      setMessage(
        "Thanh toán thành công. Cảm ơn bạn đã sử dụng dịch vụ của AutoWork"
      );
      localStorage.removeItem("latestPaymentData");

      // Xóa query params sau một khoảng thời gian
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 5000); // Xóa sau 5 giây
    }

    if (query.get("canceled")) {
      setMessage(
        "Thanh toán thất bại. Nếu có bất kỳ câu hỏi nào hãy gửi email tới autowork365@gmail.com"
      );

      // Xóa query params sau một khoảng thời gian
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }, 5000); // Xóa sau 5 giây
    }
  }, []);

  const getMembershipStatus = (planId) => {
    if (!currentMembership) return null;

    if (planId === currentMembership) {
      return "Gói hiện tại của bạn";
    }
    return null;
  };

  // Kiểm tra xem gói có thấp hơn gói hiện tại không
  const isLowerThanCurrentPlan = (planId) => {
    if (!currentMembership) return false;

    // Tìm index của gói hiện tại trong mảng plans
    const currentPlanIndex = plans.findIndex(
      (plan) => plan.id === currentMembership
    );
    // Tìm index của gói đang xem trong mảng plans
    const viewingPlanIndex = plans.findIndex((plan) => plan.id === planId);

    // Nếu không tìm thấy một trong hai gói, trả về false
    if (currentPlanIndex === -1 || viewingPlanIndex === -1) return false;

    // So sánh theo thứ tự trong mảng plans
    return viewingPlanIndex < currentPlanIndex;
  };

  const formatExpirationDate = (timestamp) => {
    if (!timestamp) return "";

    // Xử lý nhiều định dạng thời gian có thể có
    let date;
    if (typeof timestamp === "number") {
      // Timestamp dạng số (Unix timestamp)
      // Kiểm tra nếu timestamp quá lớn (dạng milliseconds)
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        // Nếu là seconds, chuyển thành milliseconds
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      // Timestamp dạng string (ISO string hoặc tùy chỉnh)
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      // Timestamp từ Firestore
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "Không xác định";
    }

    // Kiểm tra xem date có hợp lệ không
    if (isNaN(date.getTime())) {
      return "Không xác định";
    }

    // Định dạng ngày/tháng/năm và giờ
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} (${hours}:${minutes})`;
  };

  const getRemainingTime = (timestamp) => {
    if (!timestamp) return "";

    // Xử lý timestamp tương tự như formatExpirationDate
    let date;
    if (typeof timestamp === "number") {
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "";
    }

    // Kiểm tra ngày hợp lệ
    if (isNaN(date.getTime())) {
      return "";
    }

    const now = new Date();
    const diff = date.getTime() - now.getTime();

    // Nếu đã hết hạn
    if (diff < 0) {
      return "Đã hết hạn";
    }

    // Tính toán số ngày còn lại
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 30) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      return `Còn ${months} tháng ${remainingDays} ngày`;
    } else {
      return `Còn ${days} ngày`;
    }
  };

  const handleSubscribe = (plan) => {
    // Kiểm tra nếu là gói Trải nghiệm và người dùng đã có gói cao hơn
    if (plan.name === "Trải nghiệm" && currentMembership) {
      // Tìm index của gói hiện tại trong mảng plans
      const currentPlanIndex = plans.findIndex(
        (p) => p.id === currentMembership
      );
      // Tìm index của gói Trải nghiệm trong mảng plans
      const trialPlanIndex = plans.findIndex((p) => p.name === "Trải nghiệm");

      // Nếu gói hiện tại có thứ tự cao hơn gói Trải nghiệm trong mảng plans
      if (
        currentPlanIndex > trialPlanIndex &&
        currentPlanIndex !== -1 &&
        trialPlanIndex !== -1
      ) {
        alert(
          "Bạn không thể đăng ký gói Trải nghiệm khi đã sử dụng gói cao hơn."
        );
        return;
      }
    }

    trackButtonClick("subscribe_plan", {
      plan_name: plan.name,
      plan_price: plan.price,
      billing_cycle: billingCycle,
    });
    if (!checkLoginStatus()) {
      navigate("/login");
      return;
    }

    // Nếu là tiếng Việt, tiếp tục sử dụng PayOS
    if (currentLanguage === "vi") {
      createPayOSPayment(plan, billingCycle);
    } else {
      // Nếu không phải tiếng Việt, hiển thị popup chọn phương thức thanh toán
      setSelectedPaymentPlan(plan);
      setShowPaymentOptions(true);
    }
  };

  // Hàm tạo thanh toán qua PayOS
  const createPayOSPayment = async (planData, cycle = billingCycle) => {
    try {
      setPaymentLoading(true);
      const userId = localStorage.getItem("userId");
      
      let price = planData.price.replace(/\./g, "");
      if (cycle === "yearly") {
        price = planData.priceYear.replace(/\./g, "");
      } else if (cycle === "quarterly") {
        price = planData.price3Months.replace(/\./g, "");
      }

      // Xác định số điểm dựa vào plan
      const points = planData.monthlyPoints || 0;

      // Chuẩn bị dữ liệu theo đúng cấu trúc API
      const paymentData = {
        userId: userId,
        type: "plan",
        typeId:
          planData.id || plans.findIndex((p) => p.name === planData.name) + 1, // Sử dụng id hoặc index+1 làm typeId
        isYearly: cycle === "yearly",
        is3Months: cycle === "quarterly",
        amount: parseInt(price),
        returnUrl: window.location.origin + "/pricing?success=true",
        cancelUrl: window.location.origin + "/pricing?canceled=true",
        points: points, // Thêm thông tin về số điểm của plan
      };

      console.log("Dữ liệu thanh toán:");
      console.log(paymentData);

      // Gọi API tạo đơn hàng
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/create-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentData),
        }
      );

      const result = await response.json();
      console.log("Phản hồi từ API tạo thanh toán:", result);

      // Kiểm tra cấu trúc phản hồi đúng
      if (result.success && result.data && result.data.checkoutUrl) {
        // Lưu thông tin thanh toán vào localStorage để sử dụng sau khi redirect
        try {
          localStorage.setItem(
            "latestPaymentData",
            JSON.stringify({
              amount: parseInt(price),
              type: "plan",
              typeId: paymentData.typeId,
              isYearly: cycle === "yearly",
              is3Months: cycle === "quarterly",
              timestamp: new Date().getTime(),
              points: points, // Thêm thông tin về số điểm của plan
            })
          );
        } catch (e) {
          console.error(
            "Lỗi khi lưu thông tin thanh toán vào localStorage:",
            e
          );
        }

        setPaymentUrl(result.data.checkoutUrl);
        // Mở URL thanh toán trong cùng cửa sổ thay vì tab mới
        window.location.href = result.data.checkoutUrl;
      } else {
        throw new Error(
          "Không thể tạo liên kết thanh toán - Cấu trúc phản hồi không đúng"
        );
      }
    } catch (error) {
      console.error("Lỗi khi tạo thanh toán PayOS:", error);
      alert("Có lỗi xảy ra khi tạo thanh toán, vui lòng thử lại sau.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Hàm tạo thanh toán điểm qua PayOS
  const createPointsPayOSPayment = async (packageData) => {
    try {
      setPaymentLoading(true);
      const userId = localStorage.getItem("userId");
      const price = packageData.price.replace(/\./g, "");

      // Chuẩn bị dữ liệu theo đúng cấu trúc API
      const paymentData = {
        userId: userId,
        type: "points",
        typeId:
          packageData.id ||
          pointPackages.findIndex((p) => p.name === packageData.name) + 1, // Sử dụng id hoặc index+1 làm typeId
        amount: parseInt(price),
        returnUrl: window.location.origin + "/pricing?success=true",
        cancelUrl: window.location.origin + "/pricing?canceled=true",
        pointsType: "purchased", // Thêm trường này để xác định loại điểm
      };

      console.log("Dữ liệu thanh toán điểm:", paymentData);

      // Gọi API tạo đơn hàng điểm
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/create-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentData),
        }
      );

      const result = await response.json();
      console.log("Phản hồi từ API tạo thanh toán điểm:", result);

      // Kiểm tra cấu trúc phản hồi đúng
      if (result.success && result.data && result.data.checkoutUrl) {
        // Lưu thông tin thanh toán điểm vào localStorage để sử dụng sau khi redirect
        try {
          localStorage.setItem(
            "latestPaymentData",
            JSON.stringify({
              amount: parseInt(price),
              type: "points",
              typeId: paymentData.typeId,
              points: packageData.points,
              timestamp: new Date().getTime(),
              pointsType: "purchased", // Thêm trường này để xác định loại điểm
            })
          );
        } catch (e) {
          console.error(
            "Lỗi khi lưu thông tin thanh toán điểm vào localStorage:",
            e
          );
        }

        setPaymentUrl(result.data.checkoutUrl);
        // Mở URL thanh toán trong cùng cửa sổ thay vì tab mới
        window.location.href = result.data.checkoutUrl;
      } else {
        throw new Error(
          "Không thể tạo liên kết thanh toán - Cấu trúc phản hồi không đúng"
        );
      }
    } catch (error) {
      console.error("Lỗi khi tạo thanh toán điểm PayOS:", error);
      alert("Có lỗi xảy ra khi tạo thanh toán, vui lòng thử lại sau.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Hàm tạo thanh toán gói lượt Auto Comment qua PayOS
  const createCommentPayOSPayment = async (packageData) => {
    try {
      if (!checkLoginStatus()) {
        navigate("/login");
        return;
      }

      if (isCommentQuotaPurchaseLocked) {
        alert(
          "Tính năng Auto Comment và gói mua thêm chỉ áp dụng cho tài khoản Chuyên nghiệp trở lên."
        );
        return;
      }

      setPaymentLoading(true);
      const userId = localStorage.getItem("userId");
      const price = packageData.price.replace(/\./g, "");

      const paymentData = {
        userId: userId,
        type: "comment_quota",
        typeId: packageData.id,
        amount: parseInt(price),
        returnUrl: window.location.origin + "/pricing?success=true",
        cancelUrl: window.location.origin + "/pricing?canceled=true",
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/create-payment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentData),
        }
      );

      const result = await response.json();

      if (result.success && result.data && result.data.checkoutUrl) {
        try {
          localStorage.setItem(
            "latestPaymentData",
            JSON.stringify({
              amount: parseInt(price),
              type: "comment_quota",
              typeId: packageData.id,
              comments: packageData.comments,
              timestamp: new Date().getTime(),
            })
          );
        } catch (e) {
          console.error("Lỗi khi lưu thông tin thanh toán:", e);
        }

        window.location.href = result.data.checkoutUrl;
      } else {
        throw new Error("Không thể tạo liên kết thanh toán");
      }
    } catch (error) {
      console.error("Lỗi khi tạo thanh toán gói comment:", error);
      alert("Có lỗi xảy ra khi tạo thanh toán, vui lòng thử lại sau.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Kiểm tra kết quả thanh toán khi quay lại từ PayOS
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const status = queryParams.get("status");
    const paymentId = queryParams.get("paymentId");

    // Chỉ xử lý khi có status và paymentId, không xử lý cho success/canceled mới
    if (
      status &&
      paymentId &&
      !queryParams.get("success") &&
      !queryParams.get("canceled")
    ) {
      // Xác minh thanh toán với backend
      const verifyPayment = async () => {
        try {
          const response = await fetch(
            `/api/verify-payment?paymentId=${paymentId}`
          );
          const result = await response.json();

          if (result.success) {
            localStorage.removeItem("latestPaymentData");

            alert(
              "Thanh toán thành công! Tài khoản của bạn sẽ được cập nhật sau vài phút."
            );
          } else {
            alert(
              "Xác minh thanh toán không thành công, vui lòng liên hệ hỗ trợ."
            );
          }

          // Xóa query params
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        } catch (error) {
          console.error("Lỗi khi xác minh thanh toán:", error);
        }
      };

      verifyPayment();
    }
  }, [location.search]);

  const handleBuyPoints = (pkg) => {
    trackButtonClick("buy_points", {
      package_name: pkg.name,
      package_points: pkg.points,
      package_price: pkg.price,
    });
    if (!checkLoginStatus()) {
      navigate("/login");
      return;
    }

    // Nếu là tiếng Việt, tiếp tục sử dụng PayOS
    if (currentLanguage === "vi") {
      createPointsPayOSPayment(pkg);
    } else {
      // Nếu không phải tiếng Việt, hiển thị popup chọn phương thức thanh toán
      setSelectedPointPackage(pkg);
      setShowPaymentOptions(true);
    }
  };

  const handleBack = () => {
    // Nếu đến từ menu (có state.fromMenu), quay lại trang trước đó
    if (location.state?.fromMenu) {
      navigate(-1);
    } else {
      // Nếu vào trực tiếp từ URL, chuyển về landing page
      navigate("/landing-page");
    }
  };

  const handleContactClick = () => {
    trackButtonClick("contact_from_pricing");
    window.open("/landing-page#contact", "_blank");
  };

  // Thêm hàm thanh toán PayPal đơn giản
  const handlePaypalDirectPayment = async (item, isPointPackage = false) => {
    if (!userId) {
      alert("Vui lòng đăng nhập để tiến hành thanh toán");
      navigate("/login");
      return;
    }

    try {
      setPaymentLoading(true);

      // Thông tin thanh toán
      const itemName = isPointPackage
        ? `${item.points} điểm`
        : `Gói ${item.name}`;
      
      let priceInVND = "";
      if (isPointPackage) {
        priceInVND = item.price.replace(/\./g, "");
      } else {
        if (billingCycle === "yearly") {
          priceInVND = item.priceYear.replace(/\./g, "");
        } else if (billingCycle === "quarterly") {
          priceInVND = item.price3Months.replace(/\./g, "");
        } else {
          priceInVND = item.price.replace(/\./g, "");
        }
      }

      // Quy đổi USD chính xác cho PayPal (1 USD = 25.000 VND)
      const exchangeRate = 25000;
      const priceInUSD = Math.ceil(parseInt(priceInVND, 10) / exchangeRate);

      // Xác định số điểm
      let points = 0;
      if (isPointPackage) {
        points = item.points;
      } else {
        points = item.monthlyPoints || 0;
      }

      // Track the PayPal payment initiation with Meta Pixel
      trackEvent("PayPalPaymentClick", {
        button_location: "pricing_screen",
        item_name: itemName,
        item_type: isPointPackage ? "points" : "plan",
        price_usd: priceInUSD,
        price_vnd: parseInt(priceInVND),
        is_yearly: billingCycle === "yearly" && !isPointPackage,
        is_3months: billingCycle === "quarterly" && !isPointPackage,
      });

      // Track standard InitiateCheckout event for Facebook Ads optimization
      trackInitiateCheckout({
        content_name: itemName,
        content_type: isPointPackage ? "points_package" : "subscription_plan",
        content_ids: [
          isPointPackage ? `points_${item.points}` : `plan_${item.name}`,
        ],
        value: priceInUSD,
        currency: "USD",
      });

      // Lưu thông tin giao dịch vào window để xử lý sau khi thanh toán
      window.paypalTransactionData = {
        userId: userId,
        item: itemName,
        amount: priceInUSD,
        amountVND: parseInt(priceInVND),
        points: points,
        isYearly: billingCycle === "yearly" && !isPointPackage,
        is3Months: billingCycle === "quarterly" && !isPointPackage,
        type: isPointPackage ? "points" : "plan",
        timestamp: new Date().getTime(),
        status: "pending",
      };

      // Tạo URL PayPal.me với số tiền đã được tính
      const paypalMeUrl = `https://paypal.me/autowork2024/${priceInUSD}USD`;

      // Mở popup xác nhận thanh toán PayPal.me
      openPaypalMeConfirmation(paypalMeUrl, priceInUSD, itemName);
    } catch (error) {
      console.error("Lỗi khi xử lý thanh toán PayPal:", error);
      alert("Có lỗi xảy ra khi xử lý thanh toán, vui lòng thử lại sau.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Hàm mở popup xác nhận thanh toán PayPal.me
  const openPaypalMeConfirmation = (paypalMeUrl, amount, description) => {
    setShowPaymentOptions(false);

    // Track the PayPal.me URL opening
    trackEvent("PayPalMeRedirect", {
      button_location: "paypal_redirect",
      item_name: description,
      amount: amount,
    });

    // Mở cửa sổ popup mới cho PayPal.me
    window.open(paypalMeUrl, "_blank");

    // Hiển thị popup xác nhận đã thanh toán
    setMessage(
      <div className={styles.paypalConfirmMessage}>
        <p>
          Cửa sổ PayPal đã được mở. Vui lòng hoàn tất thanh toán ${amount} USD
          cho <strong>{description}</strong>.
        </p>
        <p>Sau khi thanh toán xong, vui lòng nhấn vào nút bên dưới:</p>
        <button
          className={styles.paypalConfirmButton}
          onClick={() => confirmPaypalMePayment()}
        >
          Tôi đã thanh toán
        </button>
      </div>
    );
  };

  // Hàm xác nhận đã thanh toán qua PayPal.me
  const confirmPaypalMePayment = async () => {
    try {
      // Track the payment confirmation with Meta Pixel
      trackEvent("PayPalPaymentConfirmed", {
        button_location: "payment_confirmation",
        item_name: window.paypalTransactionData?.item || "unknown",
        item_type: window.paypalTransactionData?.type || "unknown",
        amount: window.paypalTransactionData?.amount || 0,
      });

      // Track purchase event for Facebook Ads optimization
      trackPurchase({
        content_name: window.paypalTransactionData?.item || "unknown",
        content_type:
          window.paypalTransactionData?.type === "points"
            ? "points_package"
            : "subscription_plan",
        content_ids: [window.paypalTransactionData?.item || "unknown"],
        value: window.paypalTransactionData?.amount || 0,
        currency: "USD",
      });
      // Lấy dữ liệu giao dịch đã lưu
      const txData = window.paypalTransactionData;
      if (!txData) {
        console.error("Không tìm thấy dữ liệu giao dịch PayPal");
        return;
      }

      // Cập nhật trạng thái giao dịch
      txData.status = "awaiting_confirmation";
      txData.paymentMethod = "paypal.me";
      txData.completedAt = new Date().getTime();

      // Lưu giao dịch vào Firebase để admin xác nhận
      const db = getDatabase();
      const paypalTransactionsRef = ref(db, "paypalTransactions");
      const newTransactionRef = push(paypalTransactionsRef);
      await set(newTransactionRef, txData);

      // Gửi thông báo đến Telegram
      const currentTime = new Date().toLocaleString("vi-VN");
      const telegramApiUrl =
        process.env.REACT_APP_TELEGRAM_API_URL || "YOUR_TELEGRAM_BOT_API_URL";
      const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID || "YOUR_CHAT_ID";

      let message = "";

      if (txData.type === "points") {
        // Trường hợp mua điểm
        // Lấy thông tin gói điểm
        let pointPackageName = "";
        let pointPackagePrice = "";
        for (const pkg of pointPackages) {
          if (pkg.points === txData.points) {
            pointPackageName = pkg.name;
            pointPackagePrice = pkg.price;
            break;
          }
        }

        // Tạo message cho trường hợp mua điểm
        message = `
💰 MUA ĐIỂM THÀNH CÔNG - PAYPAL 💰
- User ID: ${txData.userId}
- Gói: ${pointPackageName}
- Số điểm mua thêm: +${txData.points} (không hết hạn)
- Tổng điểm hiện tại: Đang cập nhật
- Giá: ${txData.amount}$
- Thời gian: ${currentTime}`;
      } else {
        // Trường hợp nâng cấp gói dịch vụ
        // Lấy thông tin gói
        let planName = "";
        for (const plan of plans) {
          if (plan.name === txData.item.replace("Gói ", "")) {
            planName = plan.name;
            break;
          }
        }

        let cycleType = "Tháng";
        if (txData.isYearly) {
          cycleType = "Năm";
        } else if (txData.is3Months) {
          cycleType = "3 Tháng";
        }

        // Tạo message cho trường hợp nâng cấp gói
        message = `
🔔 NÂNG CẤP GÓI DỊCH VỤ - PAYPAL 🔔
- User ID: ${txData.userId}
- Gói: ${planName}
- Loại: ${cycleType}
- Giá: ${txData.amount}$
- Thời gian: ${currentTime}`;
      }

      // Gửi tin nhắn đến Telegram
      fetch(telegramApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chat_id: chatId,
          text: message,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Đã gửi thông báo đến Telegram:", data);
        })
        .catch((error) => {
          console.error("Lỗi khi gửi thông báo đến Telegram:", error);
        });

      // Hiển thị thông báo thành công
      setMessage(
        "Cảm ơn bạn đã thanh toán! Admin sẽ xác nhận giao dịch và cập nhật tài khoản của bạn trong thời gian sớm nhất."
      );

      // Xóa dữ liệu giao dịch
      delete window.paypalTransactionData;

      // Xóa thông báo sau 7 giây
      setTimeout(() => {
        setMessage(null);
      }, 7000);
    } catch (error) {
      console.error("Lỗi khi lưu giao dịch PayPal:", error);
      setMessage(
        "Có lỗi xảy ra khi xác nhận thanh toán. Vui lòng liên hệ admin để được hỗ trợ."
      );
    }
  };

  // Component popup chọn phương thức thanh toán
  const PaymentOptionsPopup = ({ onClose }) => {
    const handlePayOSPayment = () => {
      if (selectedPaymentPlan) {
        createPayOSPayment(selectedPaymentPlan, billingCycle);
      } else if (selectedPointPackage) {
        createPointsPayOSPayment(selectedPointPackage);
      }
      onClose();
    };

    const handlePayPalPayment = () => {
      // Track the PayPal button click in the payment options popup
      trackEvent("PayPalOptionClick", {
        button_location: "payment_options_popup",
        item_type: selectedPaymentPlan ? "plan" : "points",
        item_name: selectedPaymentPlan
          ? `Gói ${selectedPaymentPlan.name}`
          : selectedPointPackage
          ? `${selectedPointPackage.points} điểm`
          : "unknown",
      });

      if (selectedPaymentPlan) {
        // Thay bằng thanh toán trực tiếp qua PayPal
        handlePaypalDirectPayment(selectedPaymentPlan, false);
      } else if (selectedPointPackage) {
        // Thay bằng thanh toán trực tiếp qua PayPal
        handlePaypalDirectPayment(selectedPointPackage, true);
      }
      onClose();
    };

    return (
      <div className={styles.popupOverlay}>
        <div className={styles.popupContent}>
          <h3>Select Payment Method</h3>
          <div className={styles.paymentOptions}>
            <button
              className={styles.paymentOption}
              onClick={handlePayOSPayment}
            >
              <div className={styles.payosIcon}>VN</div>
              <span>PayOS (VN Payment)</span>
            </button>
            <button
              className={styles.paymentOption}
              onClick={handlePayPalPayment}
            >
              <FaPaypal className={styles.paypalIcon} />
              <span>PayPal</span>
            </button>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>Bảng Giá AutoWork | Phần Mềm AI Tự Động Đăng Bài Facebook</title>
        <meta
          name="description"
          content="Xem bảng giá AutoWork: các gói tự động viết và đăng bài Facebook bằng AI, phù hợp cho cá nhân, shop và doanh nghiệp."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          property="og:title"
          content="Bảng Giá AutoWork | Phần Mềm AI Tự Động Đăng Bài Facebook"
        />
        <meta
          property="og:description"
          content="Các gói dịch vụ AutoWork giúp tự động viết content và đăng bài fanpage bằng AI theo lịch."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dtqlhleqr/image/upload/v1739841345/AutoWork/logo2025_copy_bi7z3l.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Bảng Giá AutoWork | Phần Mềm AI Tự Động Đăng Bài Facebook"
        />
        <meta
          name="twitter:description"
          content="Xem các gói AutoWork để tự động hóa content marketing cho fanpage bằng AI."
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dtqlhleqr/image/upload/v1739841345/AutoWork/logo2025_copy_bi7z3l.jpg"
        />

        {/* FAQPage Schema for Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "AutoWork có miễn phí không?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "AutoWork cung cấp các gói dịch vụ đa dạng bao gồm gói Cơ bản (79.000đ/tháng), Chuyên nghiệp (179.000đ/tháng) và Cao Cấp (249.000đ/tháng) để bạn lựa chọn phù hợp với nhu cầu.",
                },
              },
              {
                "@type": "Question",
                name: "1 điểm AutoWork tương đương bao nhiêu bài viết?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Trung bình 10 điểm = 1 bài viết. Gói Cơ bản 1000 điểm/tháng tương đương ~100 bài viết.",
                },
              },
              {
                "@type": "Question",
                name: "Mua gói năm có tiết kiệm hơn không?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Có, gói năm giảm 25% so với gói tháng. Ví dụ gói Cơ bản năm chỉ 710.000đ thay vì 948.000đ.",
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <div className={styles.container}>
        <button onClick={handleBack} className={styles.backButton}>
          ← Quay lại
        </button>

        {message && (
          <div
            className={`${styles.messageAlert} ${
              typeof message === "string" && message.includes("thành công")
                ? styles.successAlert
                : styles.errorAlert
            }`}
          >
            {message}
          </div>
        )}

        {currentLanguage !== "vi" && (
          <div className={styles.paypalInfoBanner}>
            <FaPaypal className={styles.paypalBannerIcon} />
            <div className={styles.paypalBannerText}>
              <h4>Hỗ trợ thanh toán qua PayPal</h4>
              <p>
                Đối với khách hàng quốc tế, chúng tôi chấp nhận thanh toán qua
                PayPal.me và thẻ tín dụng/ghi nợ. PayPal ID:{" "}
                <strong>paypal.me/autowork2024</strong>
              </p>
            </div>
          </div>
        )}

        <h1 className={styles.title}>Chọn gói phù hợp với bạn</h1>
        <p className={styles.slogan}>
          Tự động hóa content marketing, tăng doanh thu không giới hạn
        </p>

        <div className={styles.billingToggle}>
          <span
            className={billingCycle === "monthly" ? styles.active : ""}
            onClick={() => setBillingCycle("monthly")}
          >
            {currentLanguage === "vi" ? "Theo tháng" : "Monthly"}
          </span>
          <span
            className={billingCycle === "quarterly" ? styles.active : ""}
            onClick={() => setBillingCycle("quarterly")}
          >
            {currentLanguage === "vi" ? "3 tháng (Giảm 10%)" : "3 months (10% Off)"}
          </span>
          <span
            className={billingCycle === "yearly" ? styles.active : ""}
            onClick={() => setBillingCycle("yearly")}
          >
            {currentLanguage === "vi" ? "Theo năm (Giảm 25%)" : "Yearly (25% Off)"}
          </span>
        </div>

        <div
          className={`${styles.plansContainer} ${
            billingCycle === "monthly" ? styles.monthlyMode : ""
          }`}
        >
          {plans
            .filter(
              (plan) =>
                plan.name !== "Trải nghiệm" &&
                (billingCycle !== "yearly" || !plan.isShortTerm)
            )
            .map((plan) => (
              <div
                key={plan.name}
                className={`${styles.planCard} ${
                  plan.recommended ? styles.recommended : ""
                }`}
              >
                {plan.name === "Cơ bản" && (
                  <div className={styles.recommendedBadge}>Phổ biến nhất</div>
                )}
                <h2 className={styles.planName}>
                  {plan.name}
                  {billingCycle === "yearly" && !plan.isCustom && (
                    <span className={styles.discountBadge}>
                      -{plan.yearlyDiscount}%
                    </span>
                  )}
                  {billingCycle === "quarterly" && !plan.isCustom && plan.discount3Months && (
                    <span className={styles.discountBadge}>
                      -{plan.discount3Months}%
                    </span>
                  )}
                </h2>
                <div className={styles.price}>
                  {plan.isCustom ? (
                    <span className={styles.amount}>{plan.price}</span>
                  ) : (
                    <>
                      <span className={styles.currency}>
                        {getCurrencySymbol()}
                      </span>
                      <span className={styles.amount}>
                        {
                          currentLanguage !== "vi"
                            ? billingCycle === "yearly"
                              ? plan.monthlyPriceInYearlyPlan
                                ? convertPrice(plan.monthlyPriceInYearlyPlan)
                                : "0"
                              : billingCycle === "quarterly"
                              ? plan.monthlyPriceIn3MonthsPlan
                                ? convertPrice(plan.monthlyPriceIn3MonthsPlan)
                                : "0"
                              : plan.price
                              ? convertPrice(plan.price)
                              : "0"
                            : convertPrice(
                                billingCycle === "yearly" && plan.monthlyPriceInYearlyPlan
                                  ? plan.monthlyPriceInYearlyPlan
                                  : billingCycle === "quarterly" && plan.monthlyPriceIn3MonthsPlan
                                  ? plan.monthlyPriceIn3MonthsPlan
                                  : plan.price
                              )
                        }
                      </span>
                      <span className={styles.period}>
                        {plan.isShortTerm ? "/1 ngày" : "/tháng"}
                      </span>
                    </>
                  )}
                  {billingCycle === "yearly" && !plan.isCustom && (
                    <>
                      <div className={styles.originalPrice}>
                        <span className={styles.currency}>
                          {getCurrencySymbol()}
                        </span>
                        {
                          currentLanguage !== "vi"
                            ? plan.price
                              ? convertPrice(plan.price)
                              : "0"
                            : plan.price
                            ? convertPrice(plan.price)
                            : "0"
                        }
                      </div>
                      <div className={styles.yearlyTotal}>
                        Tổng: {getCurrencySymbol()}
                        {
                          currentLanguage !== "vi"
                            ? plan.priceYear
                              ? convertPrice(plan.priceYear)
                              : "0"
                            : plan.priceYear
                            ? convertPrice(plan.priceYear)
                            : "0"
                        }
                        {currentLanguage === "vi" ? "đ" : ""}/năm
                      </div>
                    </>
                  )}
                  {billingCycle === "quarterly" && !plan.isCustom && plan.price3Months && (
                    <>
                      <div className={styles.originalPrice}>
                        <span className={styles.currency}>
                          {getCurrencySymbol()}
                        </span>
                        {
                          currentLanguage !== "vi"
                            ? plan.price
                              ? convertPrice(plan.price)
                              : "0"
                            : plan.price
                            ? convertPrice(plan.price)
                            : "0"
                        }
                      </div>
                      <div className={styles.yearlyTotal}>
                        Tổng: {getCurrencySymbol()}
                        {
                          currentLanguage !== "vi"
                            ? plan.price3Months
                              ? convertPrice(plan.price3Months)
                              : "0"
                            : plan.price3Months
                            ? convertPrice(plan.price3Months)
                            : "0"
                        }
                        {currentLanguage === "vi" ? "đ" : ""}/3 tháng
                      </div>
                    </>
                  )}
                </div>
                <ul className={styles.features}>
                  {plan.features.map((feature, index) => (
                    <li
                      key={index}
                      className={`${styles.feature} ${
                        feature.highlight ? styles.highlightFeature : ""
                      }`}
                    >
                      {/* Use FaCheckCircle icon */}
                      <span className={styles.checkmark}>
                        <FaCheckCircle />
                      </span>
                      <span>
                        {typeof feature === "string" ? feature : feature.text}
                      </span>{" "}
                      {/* Wrap feature text in span for potential styling */}
                    </li>
                  ))}
                </ul>
                <button
                  className={`${styles.button} ${
                    plan.recommended ? styles.recommendedButton : ""
                  }`}
                  onClick={() => {
                    if (plan.isCustom) {
                      handleContactClick();
                    } else {
                      handleSubscribe(plan);
                    }
                  }}
                  disabled={
                    getMembershipStatus(plan.id) !== null ||
                    isLowerThanCurrentPlan(plan.id) ||
                    (plan.name === "Trải nghiệm" && currentMembership) ||
                    paymentLoading
                  }
                >
                  {paymentLoading ? (
                    <FaSpinner className={styles.spinnerIcon} />
                  ) : plan.isCustom ? (
                    "Liên hệ ngay"
                  ) : getMembershipStatus(plan.id) ? (
                    "Gói hiện tại"
                  ) : isLowerThanCurrentPlan(plan.id) ||
                    (plan.name === "Trải nghiệm" && currentMembership) ? (
                    "Đã dùng gói cao hơn"
                  ) : (
                    "Đăng ký ngay"
                  )}
                </button>
                {getMembershipStatus(plan.id) && membershipExpiration && (
                  <div className={styles.expirationInfo}>
                    <div>
                      Hết hạn: {formatExpirationDate(membershipExpiration)}
                    </div>
                    <div className={styles.remainingTime}>
                      {getRemainingTime(membershipExpiration)}
                    </div>
                    {membershipIsYearly && (
                      <div className={styles.membershipType}>
                        (Gói hàng năm)
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>

        <div className={styles.comparisonSection}>
          <h2 className={styles.comparisonTitle}>So sánh chi tiết tính năng giữa các gói</h2>
          <p className={styles.comparisonSubtitle}>
            Xem chi tiết sự khác biệt để lựa chọn gói phù hợp nhất với nhu cầu của bạn
          </p>
          <div className={styles.tableContainer}>
            <table className={styles.comparisonTable}>
              <thead>
                <tr>
                  <th>Tính năng</th>
                  <th>Cơ bản</th>
                  <th>Chuyên nghiệp</th>
                  <th>Cao Cấp</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>Điểm bài viết hàng tháng</div>
                    <div className={styles.featureDesc}>Điểm dùng để tạo & đăng bài viết AI</div>
                  </td>
                  <td>
                    <span className={styles.pointsText}>1.000 điểm</span>
                    <br />
                    <span className={styles.pointsDesc}>(~100 bài)</span>
                  </td>
                  <td>
                    <span className={styles.pointsText}>2.900 điểm</span>
                    <br />
                    <span className={styles.pointsDesc}>(~290 bài)</span>
                  </td>
                  <td>
                    <span className={`${styles.pointsText} ${styles.highlightText}`}>4.300 điểm</span>
                    <br />
                    <span className={styles.pointsDesc}>(~430 bài)</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>Tạo bài viết từ chủ đề & từ ảnh</div>
                    <div className={styles.featureDesc}>Viết nội dung tự động dựa trên prompt hoặc hình ảnh sản phẩm</div>
                  </td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>Đăng bài tự động Fanpage Facebook</div>
                    <div className={styles.featureDesc}>Lên lịch và tự động đăng nội dung lên các Fanpage của bạn</div>
                  </td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>💬 Đăng bài tự động lên Zalo OA</div>
                    <div className={styles.featureDesc}>Tự động lên lịch và gửi/đăng bài viết lên Zalo Official Account</div>
                  </td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>🎬 Tải video tạo bài viết bằng AI</div>
                    <div className={styles.featureDesc}>Trích xuất nội dung từ video tải lên để viết bài tự động</div>
                  </td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>👁 Chế độ Duyệt bài trước khi đăng</div>
                    <div className={styles.featureDesc}>Duyệt và chỉnh sửa bài viết AI trước khi lên lịch đăng chính thức</div>
                  </td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                  <td><FaCheckCircle className={styles.checkIcon} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>🎨 🤖 Tạo ảnh AI tự động cho bài viết</div>
                    <div className={styles.featureDesc}>Tự động tạo hình ảnh minh họa chất lượng cao bằng AI (Gemini/DALL-E)</div>
                  </td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaCheckCircle className={`${styles.checkIcon} ${styles.vipFeature}`} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>🌐 Đăng bài tự động lên Website WordPress</div>
                    <div className={styles.featureDesc}>Tự động đồng bộ và xuất bản bài viết trực tiếp lên Website WordPress</div>
                  </td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaCheckCircle className={`${styles.checkIcon} ${styles.vipFeature}`} /></td>
                </tr>
                <tr>
                  <td>
                    <div className={styles.featureTitle}>🌐 AI viết bài chuyên sâu cho Website</div>
                    <div className={styles.featureDesc}>Tạo bài viết SEO chuyên sâu, chuẩn cấu trúc bài đăng Website</div>
                  </td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaTimesCircle className={styles.timesIcon} /></td>
                  <td><FaCheckCircle className={`${styles.checkIcon} ${styles.vipFeature}`} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.pointsSection}>
          <h2 className={styles.pointsTitle}>Mua thêm điểm</h2>
          <p className={styles.pointsSubtitle}>
            Điểm mua thêm được sử dụng vĩnh viễn, không có thời hạn
          </p>
          {/* <p className={styles.pointsSubtitle}>
            Cần nhiều điểm hơn? Liên hệ ngay SĐT/Zalo: 0394.932.696
          </p> */}

          <div className={styles.pointPackagesContainer}>
            {pointPackages.map((pkg) => (
              <div key={pkg.name} className={styles.pointCard}>
                <h3 className={styles.packageName}>{pkg.name}</h3>
                <div className={styles.pointAmount}>{pkg.points} điểm</div>
                <div className={styles.packagePrice}>
                  <span className={styles.currency}>{getCurrencySymbol()}</span>
                  {pkg.price ? convertPrice(pkg.price) : "0"}
                </div>
                <p className={styles.packageDescription}>{pkg.description}</p>
                <button
                  className={`${styles.button} ${styles.recommendedButton}`}
                  onClick={() => handleBuyPoints(pkg)}
                  disabled={paymentLoading}
                >
                  {paymentLoading ? (
                    <FaSpinner className={styles.spinnerIcon} />
                  ) : (
                    "Mua ngay"
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* <div id="comment-packages" className={styles.pointsSection}>
          <h2 className={styles.pointsTitle}>🤖 Mua thêm lượt Auto Comment</h2>
          <p className={styles.pointsSubtitle}>
            Chỉ áp dụng cho tài khoản Chuyên nghiệp trở lên. Lượt mua thêm được cộng dồn vào quota và không hết hạn.
          </p>
          {isCommentQuotaPurchaseLocked && (
            <p className={styles.pointsSubtitle}>
              Gói hiện tại của bạn chưa được sử dụng Auto Comment. Vui lòng nâng cấp gói trước khi mua thêm lượt.
            </p>
          )}

          <div className={styles.pointPackagesContainer}>
            {commentPackages.map((pkg) => (
              <div key={pkg.id} className={styles.pointCard}>
                <h3 className={styles.packageName}>{pkg.name}</h3>
                <div className={styles.pointAmount}>{pkg.comments.toLocaleString("vi-VN")} lượt</div>
                <div className={styles.packagePrice}>
                  <span className={styles.currency}>{getCurrencySymbol()}</span>
                  {pkg.price ? convertPrice(pkg.price) : "0"}
                </div>
                <p className={styles.packageDescription}>{pkg.description}</p>
                <button
                  className={`${styles.button} ${styles.recommendedButton}`}
                  onClick={() => createCommentPayOSPayment(pkg)}
                  disabled={paymentLoading || isCommentQuotaPurchaseLocked}
                  title={
                    isCommentQuotaPurchaseLocked
                      ? "Yêu cầu gói Chuyên nghiệp trở lên"
                      : undefined
                  }
                >
                  {paymentLoading ? (
                    <FaSpinner className={styles.spinnerIcon} />
                  ) : (
                    "Mua ngay"
                  )}
                </button>
              </div>
            ))}
          </div>
        </div> */}

        {showPaymentOptions && (
          <PaymentOptionsPopup
            onClose={() => {
              setShowPaymentOptions(false);
              setSelectedPaymentPlan(null);
              setSelectedPointPackage(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Thêm CSS cho spinner
const style = document.createElement("style");
style.innerHTML = `
  .${styles.spinnerIcon} {
    animation: spin 1s infinite linear;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);

export default PricingScreen;
